import { useImage, sortProductMedia } from '#imports'
import type { Schemas } from '#shopware'

// imgproxy options every product image shares.
const sharedModifiers = {
    fit: 'fit',
    extend: 1,
    trim: '1:ffffff:1',
    background: 'ffffff',
    // Deliberate: <NuxtImg> forwards its unset `format` prop the same way, overriding the
    // provider's webp default. Without it the URL differs from the rendered one and misses
    // its cache.
    format: undefined,
}

// ProductDetailMediaGallery's declared width/height.
const detailModifiers = { ...sharedModifiers, width: 1192, height: 1605 }

// ProductListingCard's `src` fallback — 2x its declared 345x460.
const cardModifiers = { ...sharedModifiers, width: 690, height: 920 }

/**
 * Absolute product image URLs, for anywhere an image is needed outside a rendered `<img>` —
 * structured data, feeds, meta tags, mails, tracking payloads. Saves callers from Shopware's
 * raw `media.url`, which points at the internal storage host rather than the public one.
 *
 * Each function reproduces one visible component exactly, down to the source field, so the URL
 * resolves to a rendition that component already requested. Deviating in any detail creates a
 * new imgproxy render and cache entry per product — imgproxy keys on the encoded source string,
 * so even `media.url` vs `media.path` at identical dimensions are two renditions. Keep the
 * dimensions above in sync when those components change.
 */
export function useProductImageUrl () {
    const img = useImage()

    // Listing products arrive in API order; useProduct() has already sorted the detail page's.
    const firstMedia = (product: Schemas['Product'] | undefined) =>
        sortProductMedia(product?.media)?.[0]?.media

    /**
     * In the rendition the product detail gallery uses.
     */
    function detailImageUrl (product: Schemas['Product'] | undefined): string | undefined {
        const url = firstMedia(product)?.url
        return url ? img(url, detailModifiers) : undefined
    }

    /**
     * In the rendition product listing cards use.
     */
    function cardImageUrl (product: Schemas['Product'] | undefined): string | undefined {
        const path = firstMedia(product)?.path
        return path ? img(path, cardModifiers) : undefined
    }

    return {
        detailImageUrl,
        cardImageUrl,
    }
}
